import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SDAHousingOptions = () => {
  const [openSections, setOpenSections] = useState({
    improvedLiveability: true,
    fullyAccessible: true,
    sdaHighPhysical: true,
    sdaRobust: true
  });

  const [propertyData, setPropertyData] = useState({
    youtubeUrl: 'https://youtu.be/OF-MdHjSGDo',
    bedrooms: 0,
    bathrooms: 0,
    sdaCategory: '',
    streetAddress: '',
    state: '',
    id: null
  });

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/top-home`);
        const data = await response.json();
        setPropertyData(data);
      } catch (error) {
        console.error('Error fetching property data:', error);
      }
    };

    fetchPropertyData();
  }, []);

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { 
      opacity: 1,
      height: "auto",
      transition: {
        height: {
          duration: 0.4
        },
        opacity: {
          duration: 0.3,
          delay: 0.1
        }
      }
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        height: {
          duration: 0.3
        },
        opacity: {
          duration: 0.2
        }
      }
    }
  };

  return (
    <motion.div 
      className="max-w-6xl my-10 mx-auto p-4 font-sans"
      variants={containerVariants}
    >
      <motion.h1 
        className="text-3xl font-bold text-primary mb-8 text-center"
        variants={itemVariants}
      >
        Which SDA is the right housing for you?
      </motion.h1>
      
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
        variants={containerVariants}
      >
        <motion.div 
          className=" rounded-lg overflow-hidden shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:scale-102"
          variants={itemVariants}
        >
          <button 
            className=" bg-primary w-full p-4 text-white font-semibold flex justify-between items-center transition-colors duration-300 hover:bg-purple-800"
            onClick={() => toggleSection('improvedLiveability')}
          >
            <span className="text-lg">Improved Liveability</span>
            <motion.div
              animate={{ rotate: openSections.improvedLiveability ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="text-white" />
            </motion.div>
          </button>
          
          <AnimatePresence>
            {openSections.improvedLiveability && (
              <motion.div 
                className="bg-white p-6"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
              >
                <p className="italic text-primary mb-5 border-l-4 border-primary pl-4">
                  "I need significant person-to-person support and a home that accommodates my sensory and cognitive care needs."
                </p>
                
                <p className="font-medium mb-3 text-primary">A suitable living environment for you may include:</p>
                
                <ul className="list-disc pl-8 space-y-3 text-gray-700">
                  <li className="transition-all duration-300 hover:text-primary">Open living spaces that allow for clear line-of-sight</li>
                  <li className="transition-all duration-300 hover:text-primary">Thoughtful design elements that cater to various sensory, physical, and cognitive abilities</li>
                  <li className="transition-all duration-300 hover:text-primary">Specialized lighting that adapts to sensory stimuli</li>
                  <li className="transition-all duration-300 hover:text-primary">Features that enable a high level of personalised support, tailored to your preferences.</li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        <motion.div 
          className=" rounded-lg overflow-hidden shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:scale-102"
          variants={itemVariants}
        >
          <button 
            className="bg-primary w-full p-4 text-white font-semibold flex justify-between items-center transition-colors duration-300 hover:bg-purple-800"
            onClick={() => toggleSection('fullyAccessible')}
          >
            <span className="text-lg">Fully Accessible</span>
            <motion.div
              animate={{ rotate: openSections.fullyAccessible ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="text-white" />
            </motion.div>
          </button>
          
          <AnimatePresence>
            {openSections.fullyAccessible && (
              <motion.div 
                className="bg-white p-6"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
              >
                <p className="italic text-primary mb-5 border-l-4 border-primary pl-4">
                  "I need a home that supports my physical and mobility needs while allowing me to receive the level of person-to-person assistance I require."
                </p>
                
                <p className="font-medium mb-3 text-primary">This could include:</p>
                
                <ul className="list-disc pl-8 space-y-3 text-gray-700">
                  <li className="transition-all duration-300 hover:text-primary">Design features that accommodate my mobility, such as widened doorways for wheelchair access, handrails, and a modified bathroom.</li>
                  <li className="transition-all duration-300 hover:text-primary">Automated features like motorized blinds and doors for added convenience.</li>
                  <li className="transition-all duration-300 hover:text-primary">Adjustable elements, such as height-adjustable kitchen benchtops and accessible cooking facilities.</li>
                  <li className="transition-all duration-300 hover:text-primary">A high level of personal support, tailored to my needs and preferences.</li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        <motion.div 
          className=" rounded-lg overflow-hidden shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:scale-102"
          variants={itemVariants}
        >
          <button 
            className="bg-primary w-full p-4 text-white font-semibold flex justify-between items-center transition-colors duration-300 hover:bg-purple-800"
            onClick={() => toggleSection('sdaHighPhysical')}
          >
            <span className="text-lg">SDA High Physical</span>
            <motion.div
              animate={{ rotate: openSections.sdaHighPhysical ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="text-white" />
            </motion.div>
          </button>
          
          <AnimatePresence>
            {openSections.sdaHighPhysical && (
              <motion.div 
                className="bg-white p-6"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
              >
                <p className="italic text-primary mb-5 border-l-4 border-primary pl-4">
                  "I require substantial one-on-one support and a fully accessible home with technology that promotes greater independence."
                </p>
                
                <p className="font-medium mb-3 text-primary">A suitable home for you may include:</p>
                
                <ul className="list-disc pl-8 space-y-3 text-gray-700">
                  <li className="transition-all duration-300 hover:text-primary">All the features of a fully accessible home, plus:</li>
                  <li className="transition-all duration-300 hover:text-primary">Enhanced independence for individuals with significant physical and mobility support needs</li>
                  <li className="transition-all duration-300 hover:text-primary">A completely wheelchair-accessible living space</li>
                  <li className="transition-all duration-300 hover:text-primary">Full accessibility across all areas of the home</li>
                  <li className="transition-all duration-300 hover:text-primary">Ceiling hoist installation for added support</li>
                  <li className="transition-all duration-300 hover:text-primary">Assistive technology for climate control, automation, and communication</li>
                  <li className="transition-all duration-300 hover:text-primary">High levels of personal support, customized to your needs</li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        <motion.div 
          className=" rounded-lg overflow-hidden shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:scale-102"
          variants={itemVariants}
        >
          <button 
            className="bg-primary w-full p-4 text-white font-semibold flex justify-between items-center transition-colors duration-300 hover:bg-purple-800"
            onClick={() => toggleSection('sdaRobust')}
          >
            <span className="text-lg">SDA Robust</span>
            <motion.div
              animate={{ rotate: openSections.sdaRobust ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="text-white" />
            </motion.div>
          </button>
          
          <AnimatePresence>
            {openSections.sdaRobust && (
              <motion.div 
                className="bg-white p-6"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
              >
                <p className="italic text-primary mb-5 border-l-4 border-primary pl-4">
                  "I need extensive person-to-person support and a home designed to accommodate my behaviours of concern while ensuring safety for myself and those around me."
                </p>
                
                <p className="font-medium mb-3 text-primary">You may benefit from an SDA home that offers:</p>
                
                <ul className="list-disc pl-8 space-y-3 text-gray-700">
                  <li className="transition-all duration-300 hover:text-primary">All the features of an Improved Liveability home, plus:</li>
                  <li className="transition-all duration-300 hover:text-primary">A safe and controllable environment for you, your guests, and your support network, even during times when you need assistance with communication (verbal or physical)</li>
                  <li className="transition-all duration-300 hover:text-primary">Durable, resilient materials to minimize property damage, such as reinforced walls, secure windows, soundproofing, and laminated glass</li>
                  <li className="transition-all duration-300 hover:text-primary">Private, secure spaces within the home where guests or support staff can go when you need personal space</li>
                  <li className="transition-all duration-300 hover:text-primary">High levels of person-to-person support, tailored to your needs</li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
      
      <motion.div 
        className="bg-primary text-white rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-2 shadow-xl transform transition-all duration-500 hover:shadow-2xl"
        variants={itemVariants}
      >
        <div className="p-16">
          <motion.h2 
            className="text-4xl font-bold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Find the right house with Livability Care Australia
          </motion.h2>
          
          <motion.div 
            className="flex items-center mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <span className="inline-block mr-4 text-xl">🏠</span>
            <span className="text-lg">{propertyData.bedrooms}</span>
            <span className="mx-3 text-purple-300">|</span>
            <span className="inline-block mr-4 text-xl">🛌</span>
            <span className="text-lg">{propertyData.bathrooms}</span>
          </motion.div>
          <hr></hr>
          
          <motion.div 
            className="mb-8 mt-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <p className="font-bold mb-2">Property Location: <span className="font-normal text-purple-200">{propertyData.streetAddress}, {propertyData.state}</span></p>
            <p className="font-bold">House Type: <span className="font-normal text-purple-200">{propertyData.sdaCategory}</span></p>
          </motion.div>
          
          <motion.div 
            className="space-y-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Link 
              to={`/ViewHome/${propertyData.id}`}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full font-bold block text-center transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              Book a Tour
            </Link>
            
            <Link 
              to="/properties" 
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-bold block text-center transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              Explore Vacancies
            </Link>
          </motion.div>
        </div>
        
        <motion.div 
          className="relative h-64 md:h-auto overflow-hidden"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-10 z-10 pointer-events-none"></div>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/OF-MdHjSGDo`}
            title="Property Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default SDAHousingOptions;